/**
 * Runtime — design ONE concept, end to end, with bounded repair.
 *
 * This is where OpenAI actually designs. Everything upstream (research,
 * memory, thinking, library, art direction) decided what to ask for; this asks
 * for it, judges what comes back, and asks again — up to a bound — when the
 * answer is not a design.
 *
 * Three gates, in order, each producing corrections the next attempt must fix:
 *
 *   1. SCHEMA      — the validator: is this a legal Design JSON V2 document?
 *   2. EDITOR      — editor integration: can iVisora actually open it?
 *   3. ART DIRECTOR — the document critic: is it actually a design?
 *   4. UNIQUENESS  — Design Memory: is it actually a DIFFERENT design?
 *
 * The fourth gate is why a batch of three is three concepts rather than one
 * concept three times. It is supplied by the caller, because memory belongs to
 * the run, not to a single concept.
 *
 * A rejected attempt is never silently replaced with something simpler. The
 * corrections name the exact paths that were wrong and go back to the model,
 * which is the only thing here capable of designing a fix.
 */

import { generateDesignDocuments } from '../openai';
import { renderDesignDocument } from '../renderer';
import { createEditorSession } from '../editorIntegration';
import { reviewDesignDocument } from '../artDirector';
import { resolveAssetIntents } from '../assets/resolveAssetIntents';
import { collectAssetIntents } from '../assets/assetIntent';

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/**
 * Attempts per concept: the first try plus this many repairs.
 *
 * Held at 2 (three attempts). Raising it to 4 was tried, on the theory that
 * with best-of selection the ceiling is the best of N samples. The evidence did
 * not support it: browser runs at five attempts scored 60 and 80, the same
 * range as at three. It did triple the calls per concept and drove the account
 * into sustained HTTP 429s, so the cost was real and the benefit was not.
 */
export const DEFAULT_MAX_REPAIRS = 2;

/**
 * Resolve a validator path ("pages[0].sections[1].nodes[0].fills") to the value
 * that actually sits there, so a correction can quote what the model sent.
 */
export const valueAt = (root, path) => {
  if (typeof path !== 'string' || !path) return undefined;
  return path.split('.').reduce((node, segment) => {
    if (node === undefined || node === null) return undefined;
    const key = segment.replace(/\[\d+\]/g, '');
    let cursor = key ? node[key] : node;
    (segment.match(/\[(\d+)\]/g) || []).forEach((i) => {
      cursor = cursor === undefined || cursor === null ? undefined : cursor[Number(i.slice(1, -1))];
    });
    return cursor;
  }, root);
};

/** Compact, JSON-safe rendering of an offending value. */
export const shownValue = (value) => {
  if (value === undefined) return '<missing>';
  if (value === null) return 'null';
  const json = JSON.stringify(value);
  return json.length > 120 ? `${json.slice(0, 120)}…` : json;
};

const correctionsFromFindings = (document, findings) => asArray(findings)
  .map((f) => ({
    kind: 'schema',
    path: f.path || null,
    message: f.message,
    actual: f.path ? shownValue(valueAt(document, f.path)) : null,
  }));

/**
 * Art Director findings, carried back as what was MEASURED and what to change.
 *
 * These are not schema errors and must not be repaired like them. A structural
 * fault has one wrong value at one path; a weak composition is wrong as a
 * whole, and "fix exactly this, change nothing else" produces a design with a
 * bigger headline and the same broken page. The observation is kept alongside
 * the instruction so the model can see the number it missed.
 */
const correctionsFromReview = (review) => asArray(asObject(review).revisionRequests)
  .map((r) => ({ kind: 'visual', path: null, message: r.message, request: r.request }));

/**
 * Design one concept.
 *
 * @param {Object} inputs   { plan, research, genome, patterns, constraints, priorConcepts }
 * @param {Object} [options]
 * @param {number} [options.maxRepairs]
 * @param {Object} [options.provider]
 * @param {AbortSignal} [options.signal]
 * @param {Function} [options.checkDuplicate] (document) => { duplicate, axes } — the uniqueness gate.
 * @param {Function} [options.onAttempt]  ({ attempt, stage, corrections }) — progress.
 * @returns {Promise<Object>} { ok, document, rendered, session, review, attempts, history }
 */
export const generateConcept = async (inputs = {}, options = {}) => {
  const maxRepairs = Number.isInteger(options.maxRepairs) ? options.maxRepairs : DEFAULT_MAX_REPAIRS;
  // Injectable so tests drive the image flow without a network, and so a caller
  // can supply its own provider configuration.
  const resolveImages = typeof options.resolveImages === 'function'
    ? options.resolveImages
    : (pages) => resolveAssetIntents(pages, asObject(options.assetOptions));
  const history = [];
  let corrections = [];
  /** The highest-scoring attempt seen so far — see the best-of note below. */
  let best = null;

  for (let attempt = 0; attempt <= maxRepairs; attempt += 1) {
    if (options.signal && options.signal.aborted) break;
    if (typeof options.onAttempt === 'function') options.onAttempt({ attempt, corrections });

    // One document per request. Asking for N in one call is how concepts end up
    // as variations of each other; each concept gets its own act of design.
    const { results } = await generateDesignDocuments(
      { ...inputs, corrections },
      { count: 1, provider: options.provider, signal: options.signal, model: options.model },
    );

    const first = asObject(results[0]);
    const document = first.document;
    const validation = asObject(first.validation);

    // Gate 1 — schema.
    if (!validation.valid) {
      corrections = correctionsFromFindings(document, validation.errors);
      history.push({ attempt, stage: 'schema', accepted: false, findings: corrections });
      continue;
    }

    // Resolve the designer's image intents to real photographs BEFORE anything
    // judges or opens the page. The model said what imagery the design needs;
    // this only finds it. Placing it here means the Art Director reviews the
    // page an operator will actually see, rather than one full of placeholders.
    const renderedRaw = renderDesignDocument(document);
    let assets = { pages: renderedRaw.pages, resolved: 0, unresolved: [], providers: [] };
    try {
      assets = await resolveImages(renderedRaw.pages);
    } catch (err) {
      // A misconfigured or unreachable image service is an operator problem,
      // not a design problem: record it and let the imagery rules report the
      // unresolved intents. Failing the generation here would hide the cause.
      assets = {
        pages: renderedRaw.pages,
        resolved: 0,
        unresolved: collectAssetIntents(renderedRaw.pages).map(({ id, intent }) => ({
          id, role: intent.role, required: intent.required, reason: 'service-unavailable', triedQueries: [],
        })),
        providers: [],
        error: err && err.message,
      };
    }
    const rendered = { ...renderedRaw, pages: assets.pages };

    // Gate 2 — will the editor open it?
    const session = createEditorSession(rendered, asObject(options.sessionOptions));
    if (!session.ok) {
      corrections = correctionsFromFindings(document, session.errors);
      history.push({ attempt, stage: 'editor', accepted: false, findings: corrections });
      continue;
    }

    // Gate 3 — is it a design? The rendered page goes in with it, so the Art
    // Director judges what this will look like rather than only what it says.
    const review = reviewDesignDocument(document, { ...inputs, rendered, assets }, { thresholds: options.thresholds });

    // Remember the strongest attempt so far.
    //
    // Repair is not monotonic: asked to fix a hierarchy, the model sometimes
    // returns a simpler page that fixes it by having less on it. A real run
    // produced a 24-node composition on one attempt and a 9-node one on the
    // next, and the loop shipped the 9 — the better design had been generated
    // and thrown away. When the budget runs out, the best attempt is the honest
    // thing to return, not the most recent one.
    if (!best || review.scores.creative > best.review.scores.creative) {
      best = { document, rendered, session: session.session, review, assets };
    }

    if (!review.approved && attempt < maxRepairs) {
      corrections = correctionsFromReview(review);
      history.push({ attempt, stage: 'artDirector', accepted: false, findings: corrections, review });
      continue;
    }

    // Budget exhausted and still not approved: fall back to the best attempt
    // rather than the last, unless this one IS the best.
    if (!review.approved && best.document !== document) {
      history.push({
        attempt, stage: 'best-of', accepted: false, review,
        note: `attempt ${attempt} scored ${review.scores.creative}; returning the stronger attempt at ${best.review.scores.creative}`,
      });
      return {
        ok: true,
        document: best.document,
        rendered: best.rendered,
        session: best.session,
        review: best.review,
        assets: best.assets,
        approvedOnMerit: false,
        attempts: attempt + 1,
        history,
      };
    }

    // Gate 4 — is it a DIFFERENT design? A concept that repeats one already
    // produced in this run is not a second concept, however valid it is.
    const duplication = typeof options.checkDuplicate === 'function'
      ? asObject(options.checkDuplicate(document))
      : {};
    if (duplication.duplicate && attempt < maxRepairs) {
      const axes = asArray(duplication.axes);
      corrections = [{
        path: null,
        message: `This concept repeats one already produced in this run${axes.length ? ` (${axes.join(', ')} are the same)` : ''}. Design a genuinely different composition — change structure, hierarchy, imagery strategy or decorative language, not just colour or wording.`,
      }];
      history.push({ attempt, stage: 'uniqueness', accepted: false, findings: corrections });
      continue;
    }

    history.push({ attempt, stage: 'accepted', accepted: true, review, warnings: asArray(validation.warnings) });
    return {
      ok: true,
      document,
      rendered,
      session: session.session,
      review,
      // True when the last attempt was accepted only because the repair budget
      // ran out — the caller decides whether that is good enough to show.
      approvedOnMerit: review.approved,
      // True when the concept was accepted despite repeating an earlier one,
      // because the repair budget ran out.
      duplicate: Boolean(duplication.duplicate),
      attempts: attempt + 1,
      warnings: asArray(validation.warnings),
      history,
    };
  }

  return { ok: false, attempts: history.length, corrections, history };
};
